<template>
    <div class="page-container">
        <div class="notice-list-card">
            <div class="section-header">
                <div class="section-title-wrap">
                    <div class="section-title">등록된 공지</div>
                    <div class="section-count">총 {{ totalCount }} 건</div>
                </div>
            </div>

            <div class="notice-list-table-wrap">
                <table class="notice-list-table">
                    <colgroup>
                        <col class="col-expand" />
                        <col class="col-notice-no" />
                        <col class="col-notice-sites" />
                        <col class="col-notice-message" />
                        <col class="col-notice-date" />
                        <col class="col-notice-status" />
                        <col class="col-notice-actions" />
                    </colgroup>

                    <thead>
                        <tr>
                            <th></th>
                            <th>No</th>
                            <th>등록지</th>
                            <th>내용</th>
                            <th>등록일</th>
                            <th>상태</th>
                            <th>관리</th>
                        </tr>
                    </thead>

                    <tbody>
                        <template v-for="item in noticeList" :key="item.id">
                            <tr class="notice-row"
                                :class="{ 'row-active': item.expanded }">
                                <td>
                                    <button class="icon-btn" @click="toggleNoticeExpand(item.id)">
                                        {{ item.expanded ? '−' : '+' }}
                                    </button>
                                </td>
                                <td>{{ item.id }}</td>
                                <td class="td-left" :title="getSiteNamesText(item.siteIds)">
                                    {{ getSiteSummary(item.siteIds) }}
                                </td>
                                <td class="td-left td-ellipsis" :title="item.content">
                                    {{ item.content }}
                                </td>
                                <td>{{ item.createdAt }}</td>
                                <td>
                                    <span class="status-badge">
                                        게시중
                                    </span>
                                </td>
                                <td>
                                    <div class="table-action-wrap">
                                        <button class="table-chip-btn is-edit" @click="onEditNotice(item)">
                                            수정
                                        </button>
                                        <button class="table-chip-btn is-delete" @click="onDeleteNotice(item.id)">
                                            삭제
                                        </button>
                                    </div>
                                </td>
                            </tr>

                            <tr v-if="item.expanded" class="detail-row">
                                <td colspan="7" class="detail-cell">
                                    <div class="detail-panel">
                                        <div class="detail-section">
                                            <div class="detail-title">공지 내용</div>
                                            <div class="detail-message-box">
                                                {{ item.content }}
                                            </div>
                                        </div>

                                        <div class="detail-section">
                                            <div class="detail-title">등록 공사 확인</div>
                                            <div class="detail-site-grid">
                                                <label v-for="site in allSites" :key="site.id" class="detail-site-item">
                                                    <input
                                                        type="checkbox"
                                                        :checked="item.siteIds.includes(site.id)"
                                                        disabled
                                                    />
                                                    <span>{{ site.name }}</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>

                        <tr v-if="!noticeList.length">
                            <td colspan="7" class="empty-cell">등록된 공지가 없습니다.</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="notice-pagination-wrap">
                <PaginationBar
                    :currentPage="currentPage"
                    :totalPages="totalPages"
                    :groupSize="groupSize"
                    @change-page="goToPage"
                />
            </div>
        </div>

        <div class="register-layout">
            <div class="register-card receiver-card">
                <div class="section-header">
                    <div class="section-title">수신자 선택</div>
                </div>

                <div class="panel-body">
                    <div class="table-scroll-area">
                        <table class="receiver-table">
                            <colgroup>
                                <col class="col-check" />
                                <col class="col-name" />
                            </colgroup>

                            <thead>
                                <tr>
                                    <th>
                                        <input type="checkbox" :checked="isAllLeftSelected" @change="toggleAllLeft" />
                                    </th>
                                    <th>공사명</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="item in leftReceivers" :key="item.id" class="item-row">
                                    <td class="check-cell">
                                        <input
                                            type="checkbox"
                                            :checked="item.selected"
                                            @change="toggleLeftItem(item.id)"
                                        />
                                    </td>
                                    <td class="item-name-cell">{{ item.name }}</td>
                                </tr>

                                <tr v-if="!leftReceivers.length">
                                    <td colspan="2" class="empty-cell">이동 가능한 공사 현장이 없습니다.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="move-button-wrap">
                <button class="move-btn" @click="moveToRight">›</button>
                <button class="move-btn" @click="moveToLeft">‹</button>
            </div>

            <div class="register-card receiver-card">
                <div class="section-header">
                    <div class="section-title">수신자</div>
                </div>

                <div class="panel-body">
                    <div class="table-scroll-area">
                        <table class="receiver-table">
                            <colgroup>
                                <col class="col-check" />
                                <col class="col-name" />
                            </colgroup>

                            <thead>
                                <tr>
                                    <th>
                                        <input type="checkbox" :checked="isAllRightSelected" @change="toggleAllRight" />
                                    </th>
                                    <th>공사명</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="item in rightReceivers" :key="item.id" class="item-row">
                                    <td class="check-cell">
                                        <input
                                            type="checkbox"
                                            :checked="item.selected"
                                            @change="toggleRightItem(item.id)"
                                        />
                                    </td>
                                    <td class="item-name-cell">{{ item.name }}</td>
                                </tr>

                                <tr v-if="!rightReceivers.length">
                                    <td colspan="2" class="empty-cell">선택된 수신자가 없습니다.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="register-card editor-card">
                <div class="section-header">
                    <div class="section-title">
                        {{ editingNoticeId ? '공지 수정' : '공지내용' }}
                    </div>
                </div>

                <div class="panel-body editor-panel-body">
                    <textarea
                        v-model="noticeMessage"
                        class="notice-textarea"
                        placeholder="공지 내용을 입력해주세요"
                    ></textarea>

                    <div class="editor-footer">
                        <div class="receiver-count-text">
                            선택 수신자 {{ selectedReceiverCount }} 명
                        </div>

                        <div class="editor-action-wrap">
                            <button v-if="editingNoticeId" class="secondary-btn" @click="cancelEdit">
                                취소
                            </button>

                            <button class="primary-btn submit-btn" @click="onSubmit">
                                {{ editingNoticeId ? '수정완료' : '게시' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showModal" class="modal-overlay" @click="closeModal">
            <div class="modal-card" @click.stop>
                <div class="modal-icon">✓</div>
                <div class="modal-title">
                    {{ editingNoticeId ? '수정 완료' : '게시 완료' }}
                </div>
                <div class="modal-desc">
                    공지 사항이 정상적으로 처리되었습니다.
                </div>

                <div class="modal-button-row">
                    <button class="primary-btn modal-btn" @click="closeModal">
                        확인
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/api/axios'
import PaginationBar from '@/components/admin/PaginationBar.vue'

export default {
    name: 'EmergencyNotice',

    components: {
        PaginationBar,
    },

    data() {
        return {
            noticeMessage: '',
            showModal: false,
            editingNoticeId: null,

            allSites: [],
            noticeList: [],

            leftReceivers: [],
            rightReceivers: [],

            loading: false,
            currentPage: 0,
            totalPages: 0,
            totalCount: 0,
            size: 10,
            groupSize: 5,
            accessibleSiteIds: [],
        }
    },

    computed: {
        isAllLeftSelected() {
            return this.leftReceivers.length > 0 && this.leftReceivers.every(item => item.selected)
        },

        isAllRightSelected() {
            return this.rightReceivers.length > 0 && this.rightReceivers.every(item => item.selected)
        },

        selectedReceiverCount() {
            return this.rightReceivers.length
        },
    },

    async mounted() {
        await this.fetchAccessibleSiteIds()
        await this.fetchAllSites()
        this.resetEditor()
        await this.fetchNoticeList()
    },

    methods: {
        async fetchAccessibleSiteIds() {
            try {
                const res = await api.get('/app/site/myList')
                const list = res.data?.result || []

                this.accessibleSiteIds = list
                .map(item => Number(item.id))
                .filter(id => !Number.isNaN(id))
            } catch (e) {
                console.error('접근 가능 현장 조회 실패', e)
                this.accessibleSiteIds = []
            }
        },
        async fetchAllSites() {
            try {
                if (!this.accessibleSiteIds.length) {
                    this.allSites = []
                    return
                }

                const response = await api.get('/app/site/list', {
                    params: {
                        orderBy: 'createdAtAsc',
                        deleted: false,
                        siteIds: this.accessibleSiteIds.join(',')  // 🔥 핵심
                    },
                })

                const result = response?.data?.result || []

                this.allSites = result.map(item => ({
                    id: item.id,
                    name: item.name,
                }))
            } catch (error) {
                console.error('현장 목록 조회 실패', error)
                this.allSites = []
            }
        },

        async fetchNoticeList() {
            this.loading = true

            try {
                const response = await api.get('/app/emergencyNotice/pagingList', {
                    params: {
                        page: this.currentPage,
                        size: this.size,
                        orderBy: 'createdAtDesc',
                        deleted: false,
                    },
                })

                const pageData = response?.data?.result

                this.noticeList = Array.isArray(pageData?.content)
                    ? pageData.content.map(item => ({
                          id: item.id,
                          content: item.content,
                          createdAt: this.formatDateTime(item.createdAt),
                          siteIds: item.siteIds || [],
                          expanded: false,
                      }))
                    : []

                this.totalCount = Number(pageData?.totalElements ?? 0)
                this.totalPages = Number(pageData?.totalPages ?? 0)
                this.currentPage = Number(pageData?.number ?? 0)

                if (this.currentPage > 0 && this.noticeList.length === 0) {
                    this.currentPage = this.currentPage - 1
                    await this.fetchNoticeList()
                    return
                }
            } catch (error) {
                console.error('긴급공지 목록 조회 실패', error)
                this.noticeList = []
                this.totalCount = 0
                this.totalPages = 0
            } finally {
                this.loading = false
            }
        },

        goToPage(pageNo) {
            if (pageNo < 0 || pageNo >= this.totalPages || pageNo === this.currentPage) {
                return
            }

            this.currentPage = pageNo
            this.fetchNoticeList()
        },

        toggleLeftItem(itemId) {
            this.leftReceivers = this.leftReceivers.map(item =>
                item.id === itemId ? { ...item, selected: !item.selected } : item
            )
        },

        toggleRightItem(itemId) {
            this.rightReceivers = this.rightReceivers.map(item =>
                item.id === itemId ? { ...item, selected: !item.selected } : item
            )
        },

        toggleAllLeft(event) {
            const checked = event.target.checked
            this.leftReceivers = this.leftReceivers.map(item => ({ ...item, selected: checked }))
        },

        toggleAllRight(event) {
            const checked = event.target.checked
            this.rightReceivers = this.rightReceivers.map(item => ({ ...item, selected: checked }))
        },

        moveToRight() {
            const moveItems = this.leftReceivers
                .filter(item => item.selected)
                .map(item => ({ ...item, selected: false }))

            if (!moveItems.length) return

            const remainItems = this.leftReceivers.filter(item => !item.selected)
            const existingIds = new Set(this.rightReceivers.map(item => item.id))
            const uniqueMoveItems = moveItems.filter(item => !existingIds.has(item.id))

            this.leftReceivers = remainItems
            this.rightReceivers = [...this.rightReceivers, ...uniqueMoveItems]
        },

        moveToLeft() {
            const moveItems = this.rightReceivers
                .filter(item => item.selected)
                .map(item => ({ ...item, selected: false }))

            if (!moveItems.length) return

            const remainItems = this.rightReceivers.filter(item => !item.selected)
            const existingIds = new Set(this.leftReceivers.map(item => item.id))
            const uniqueMoveItems = moveItems.filter(item => !existingIds.has(item.id))

            this.rightReceivers = remainItems
            this.leftReceivers = [...this.leftReceivers, ...uniqueMoveItems]
        },

        getSiteSummary(siteIds) {
            const names = this.allSites
                .filter(site => siteIds.includes(site.id))
                .map(site => site.name)

            if (!names.length) return '-'
            if (names.length === 1) return names[0]
            return `${names[0]} 외 ${names.length - 1}건`
        },

        getSiteNamesText(siteIds) {
            return this.allSites
                .filter(site => siteIds.includes(site.id))
                .map(site => site.name)
                .join(', ')
        },

        toggleNoticeExpand(noticeId) {
            this.noticeList = this.noticeList.map(item =>
                item.id === noticeId
                    ? { ...item, expanded: !item.expanded }
                    : item
            )
        },

        onEditNotice(item) {
            this.editingNoticeId = item.id
            this.noticeMessage = item.content

            const selectedIds = new Set(item.siteIds)

            this.rightReceivers = this.allSites
                .filter(site => selectedIds.has(site.id))
                .map(site => ({ ...site, selected: false }))

            this.leftReceivers = this.allSites
                .filter(site => !selectedIds.has(site.id))
                .map(site => ({ ...site, selected: false }))
        },

        async onDeleteNotice(noticeId) {

            const firstOk = window.confirm(
                '해당 공지를 삭제하시겠습니까?\n\n삭제 후 목록에서 표시되지 않습니다.'
            )

            if (!firstOk) return

            const secondOk = window.confirm(
                '정말로 삭제하시겠습니까?\n\n삭제 시 복구가 불가합니다.'
            )

            if (!secondOk) return

            try {
                await api.delete('/app/emergencyNotice/delete', {
                    data: {
                        id: noticeId,
                    },
                })

                alert('긴급공지가 삭제되었습니다.')

                if (this.editingNoticeId === noticeId) {
                    this.cancelEdit()
                }

                await this.fetchNoticeList()

            } catch (error) {
                console.error('긴급공지 삭제 실패', error)
                alert('긴급공지 삭제 실패')
            }
        },

        async onSubmit() {
            if (this.selectedReceiverCount === 0) {
                window.alert('수신자를 선택해주세요')
                return
            }

            if (!this.noticeMessage.trim()) {
                window.alert('공지 내용을 입력해주세요')
                return
            }

            const selectedSiteIds = this.rightReceivers.map(item => item.id)

            try {
                if (this.editingNoticeId) {
                    await api.post('/app/emergencyNotice/update', {
                        id: this.editingNoticeId,
                        content: this.noticeMessage.trim(),
                        siteIds: selectedSiteIds,
                    })
                } else {
                    await api.post('/app/emergencyNotice/create', {
                        content: this.noticeMessage.trim(),
                        siteIds: selectedSiteIds,
                    })
                }

                this.resetEditor()
                await this.fetchNoticeList()
                this.showModal = true
            } catch (error) {
                console.error('긴급공지 저장 실패', error)
                alert('긴급공지 저장 실패')
            }
        },

        cancelEdit() {
            this.resetEditor()
        },

        resetEditor() {
            this.editingNoticeId = null
            this.noticeMessage = ''
            this.rightReceivers = []
            this.leftReceivers = this.allSites.map(site => ({
                ...site,
                selected: false,
            }))
        },

        closeModal() {
            this.showModal = false
        },

        formatDateTime(value) {
            if (!value) return '-'

            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return value

            const yyyy = date.getFullYear()
            const mm = String(date.getMonth() + 1).padStart(2, '0')
            const dd = String(date.getDate()).padStart(2, '0')
            const hh = String(date.getHours()).padStart(2, '0')
            const mi = String(date.getMinutes()).padStart(2, '0')

            return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
        },
    },
}
</script>

<style scoped>
.page-container {
    width: 100%;
    height: 100%;
    min-height: 0;
    padding: 20px;
    box-sizing: border-box;
    background: #f3f4f6;
    overflow: auto;
}

.notice-list-card,
.register-card {
    border: 1px solid #e5e7eb;
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
    overflow: hidden;
}

.notice-list-card {
    margin-bottom: 18px;
    min-height: 290px;
    display: flex;
    flex-direction: column;
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #edf0f4;
    background: #ffffff;
}

.section-title-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.section-title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
}

.section-count {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.2;
}

.notice-list-table-wrap,
.panel-body {
    padding: 16px;
}

.notice-list-table-wrap {
    flex: 1;
    min-height: 0;
    overflow: auto;
}

.notice-list-table,
.receiver-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
}

.notice-list-table {
    min-width: 980px;
}

.receiver-table {
    min-width: 100%;
}

.notice-list-table th,
.receiver-table th {
    height: 44px;
    background: #f8fafc;
    color: #374151;
    font-size: 13px;
    font-weight: 700;
    text-align: center;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
}

.notice-list-table th:first-child,
.receiver-table th:first-child {
    border-left: 1px solid #e5e7eb;
    border-top-left-radius: 10px;
}

.notice-list-table th:last-child,
.receiver-table th:last-child {
    border-right: 1px solid #e5e7eb;
    border-top-right-radius: 10px;
}

.notice-list-table td,
.receiver-table td {
    min-height: 44px;
    padding: 10px 12px;
    border-bottom: 1px solid #eef2f7;
    color: #374151;
    font-size: 14px;
    text-align: center;
    background: #ffffff;
    vertical-align: middle;
    line-height: 1.45;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    word-break: keep-all;
    overflow-wrap: anywhere;
}

/* 전체 테이블 행 hover active */
.notice-list-table tbody tr,
.receiver-table tbody tr {
    transition:
        background-color 0.15s ease,
        box-shadow 0.15s ease;
}

.notice-list-table tbody tr:hover td,
.receiver-table tbody tr:hover td {
    background: #f8fafc;
}

/* =========================
   TABLE ROW ACTIVE
========================= */

.notice-list-table tbody tr.notice-row:hover,
.receiver-table tbody tr.item-row:hover {
    box-shadow: inset 4px 0 0 var(--color-primary);
}

/* 펼쳐진 공지 */

.notice-list-table tbody tr.detail-row td {
    background: #fff7ed;
}

.td-left,
.item-name-cell {
    text-align: left;
}

/* 말줄임 제거 */
.td-ellipsis {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    word-break: keep-all;
    overflow-wrap: anywhere;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 68px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    background: #eaf7ee;
    color: #1f8b4c;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
}

.table-action-wrap,
.editor-action-wrap,
.modal-button-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
}

.primary-btn,
.secondary-btn,
.table-chip-btn,
.icon-btn,
.move-btn {
    cursor: pointer;
    transition: all 0.18s ease;
}

.primary-btn,
.secondary-btn {
    min-width: 72px;
    height: 36px;
    padding: 0 14px;
    border-radius: 8px;
    font-size: 13px;
    line-height: 1;
    text-align: center;
}

.primary-btn {
    border: 1px solid transparent;
    background: var(--color-primary);
    color: var(--color-text-inner);
}

.primary-btn:hover {
    background: var(--color-primary-border);
}

.secondary-btn {
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #374151;
}

.secondary-btn:hover {
    background: #f3f4f6;
}

.table-chip-btn {
    min-width: 32px;
    height: 28px;
    padding: 0 10px;
    border: 1px solid transparent;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;

    transition:
        background-color .18s ease,
        border-color .18s ease,
        color .18s ease,
        transform .12s ease,
        box-shadow .18s ease;
}

.icon-btn {
    width: 34px;
    height: 34px;
    border: 1px solid #d1d5db;
    border-radius: 9px;
    background: #ffffff;
    color: #374151;
    font-size: 20px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.icon-btn:hover {
    background: #f3f4f6;
}

.table-chip-btn.is-edit {
    background: #396cda;
    color: #ffffff;
    border-color: #396cda;
}

.table-chip-btn.is-edit:hover {
    background: #2f5fc7;
    border-color: #2f5fc7;

    transform: translateY(-2px);

    box-shadow:
        0 10px 22px rgba(59,130,246,.30);
}

.table-chip-btn.is-delete {
    background: #ef4444;
    color: #fff;
    border-color: #ef4444;
}

.table-chip-btn.is-delete:hover {
    background: #dc2626;
    border-color: #dc2626;

    transform: translateY(-2px);

    box-shadow:
        0 10px 22px rgba(239,68,68,.35);
}

.detail-row td.detail-cell {
    padding: 0;
    background: #f8fafc;
}

.detail-panel {
    display: grid;
    grid-template-columns: minmax(280px, 320px) 1fr;
    gap: 18px;
    padding: 18px;
    background: #f8fafc;
}

.detail-section {
    padding: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    background: #ffffff;
}

.detail-title {
    margin-bottom: 12px;
    font-size: 15px;
    font-weight: 700;
    color: #111827;
}

.detail-message-box {
    min-height: 88px;
    padding: 14px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #f9fafb;
    line-height: 1.6;
    color: #374151;
    word-break: keep-all;
    overflow-wrap: anywhere;
}

.detail-site-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(160px, 1fr));
    gap: 10px 14px;
}

.detail-site-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #374151;
    font-size: 14px;
    line-height: 1.4;
}

/* 하단 등록 영역 */
.register-layout {
    display: grid;
    grid-template-columns:
        minmax(300px, 1fr)
        60px
        minmax(300px, 1fr)
        minmax(360px, 1.05fr);
    gap: 18px;
    align-items: stretch;
    min-width: 1120px;
}

.receiver-card {
    min-height: 420px;
    display: flex;
    flex-direction: column;
}

.table-scroll-area {
    max-height: 360px;
    overflow: auto;
}

.col-expand {
    width: 56px;
}

.col-check {
    width: 46px;
}

.col-name {
    width: auto;
}

.col-notice-no {
    width: 72px;
}

.col-notice-sites {
    width: 250px;
}

.col-notice-message {
    width: auto;
}

.col-notice-date {
    width: 170px;
}

.col-notice-status {
    width: 120px;
}

.col-notice-actions {
    width: 150px;
}

.check-cell {
    text-align: center;
}

.move-button-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
}

.move-btn {
    width: 46px;
    height: 46px;
    border: none;
    border-radius: 10px;
    background: var(--color-primary);
    color: var(--color-text-inner);
    font-size: 28px;
    font-weight: 700;
    text-align: center;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-items: flex-start;
}

.move-btn:hover {
    background: var(--color-primary-border);
}

.editor-card {
    display: flex;
    flex-direction: column;
}

.editor-panel-body {
    display: flex;
    flex-direction: column;
    height: calc(100% - 58px);
}

.notice-textarea {
    width: 100%;
    flex: 1;
    min-height: 240px;
    padding: 16px;
    box-sizing: border-box;
    border: 1px solid #dbe2ea;
    border-radius: 12px;
    background: #f9fafb;
    font-size: 14px;
    line-height: 1.7;
    color: #111827;
    resize: none;
    outline: none;
}

.editor-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 14px;
    gap: 12px;
    flex-wrap: wrap;
}

.receiver-count-text {
    font-size: 14px;
    font-weight: 600;
    color: #6b7280;
}

.submit-btn,
.modal-btn {
    min-width: 92px;
}

.modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.28);
}

.modal-card {
    width: 360px;
    padding: 28px 24px 22px;
    border-radius: 22px;
    background: #ffffff;
    box-shadow: 0 20px 45px rgba(15, 23, 42, 0.18);
    text-align: center;
}

.modal-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 62px;
    height: 62px;
    margin: 0 auto 14px;
    border-radius: 50%;
    background: #eef4ff;
    color: #2f66f3;
    font-size: 30px;
    font-weight: 800;
}

.modal-title {
    margin-bottom: 8px;
    font-size: 22px;
    font-weight: 800;
    color: #111827;
}

.modal-desc {
    font-size: 14px;
    line-height: 1.6;
    color: #6b7280;
}

.empty-cell {
    padding: 28px 12px;
    color: #8a94a6;
    text-align: center;
}

.notice-pagination-wrap {
    padding: 0 16px 16px;
    flex-shrink: 0;
}

@media (max-width: 1600px) {
    .page-container {
        padding: 18px;
    }

    .register-layout {
        grid-template-columns:
            minmax(280px, 1fr)
            56px
            minmax(280px, 1fr)
            minmax(340px, 0.95fr);
        gap: 14px;
        min-width: 1040px;
    }

    .receiver-card {
        min-height: 390px;
    }

    .table-scroll-area {
        max-height: 330px;
    }

    .col-notice-sites {
        width: 220px;
    }

    .col-notice-date {
        width: 150px;
    }

    .col-notice-actions {
        width: 140px;
    }

    .notice-list-table td,
    .receiver-table td {
        font-size: 13px;
        padding: 9px 10px;
    }

    .notice-list-table th,
    .receiver-table th {
        font-size: 12px;
    }

    .section-title {
        font-size: 17px;
    }

    .move-btn {
        width: 42px;
        height: 42px;
        font-size: 26px;
    }
}

@media (max-width: 1400px) {
    .page-container {
        padding: 16px;
    }

    .notice-list-card {
        min-height: 270px;
    }

    .register-layout {
        grid-template-columns:
            minmax(260px, 1fr)
            52px
            minmax(260px, 1fr)
            minmax(320px, 0.92fr);
        gap: 12px;
        min-width: 960px;
    }

    .receiver-card {
        min-height: 360px;
    }

    .table-scroll-area {
        max-height: 300px;
    }

    .section-header {
        padding: 14px 16px;
    }

    .notice-list-table-wrap,
    .panel-body {
        padding: 12px;
    }

    .notice-list-table {
        min-width: 900px;
    }

    .col-notice-sites {
        width: 200px;
    }

    .col-notice-date {
        width: 140px;
    }

    .col-notice-status {
        width: 110px;
    }

    .col-notice-actions {
        width: 130px;
    }

    .notice-list-table td,
    .receiver-table td {
        font-size: 13px;
        padding: 8px 9px;
    }

    .section-title {
        font-size: 16px;
    }

    .section-count,
    .receiver-count-text {
        font-size: 13px;
    }

    .primary-btn,
    .secondary-btn {
        min-width: 68px;
        height: 34px;
        font-size: 12px;
    }

    .icon-btn {
        width: 32px;
        height: 32px;
        font-size: 18px;
    }

    .move-btn {
        width: 40px;
        height: 40px;
        font-size: 24px;
    }

    .notice-textarea {
        font-size: 13px;
    }
}

@media (max-width: 1200px) {
    .detail-panel {
        grid-template-columns: 1fr;
    }
}

.notice-list-table tbody tr.row-active td {
    background: #fff7ed;
}

.notice-list-table tbody tr.row-active {
    box-shadow: inset 4px 0 0 var(--color-primary);
}
</style>